<template>
  <div class="chat-text-block text-base font-normal leading-6 text-light-fg-default" v-html="renderedHtml" />
</template>

<script setup lang="ts">
import MarkdownIt from 'markdown-it';

interface Props {
  markdown: string;
}

const props = defineProps<Props>();

const md = new MarkdownIt({
  html: false, // HTMLタグをエスケープ（v-htmlでのXSS防止）
  linkify: true, // URL文字列を自動で<a>タグに変換
  breaks: true, // 改行文字を<br>に変換（Markdown標準では末尾スペース2つが必要）
});

// すべてのリンクを別タブで開く
const defaultRender = md.renderer.rules.link_open || ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet('target', '_blank');
  tokens[idx].attrSet('rel', 'noopener noreferrer');
  return defaultRender(tokens, idx, options, env, self);
};

const renderedHtml = computed(() => md.render(props.markdown));
</script>

<style lang="scss" scoped>
.chat-text-block {
  :deep(h1) {
    @apply pb-2 pt-3 text-lg font-bold text-light-fg-emphasis;
  }

  :deep(h2) {
    @apply pb-1 pt-2 text-base font-bold text-light-fg-emphasis;
  }

  :deep(h3) {
    @apply pb-1 pt-2 text-sm font-bold text-light-fg-emphasis;
  }

  :deep(p) {
    @apply py-0.5;
  }

  :deep(a) {
    @apply text-light-fg-accent underline;
  }

  :deep(ul) {
    @apply my-1 list-disc pl-5;
  }

  :deep(ol) {
    @apply my-1 list-decimal pl-5;
  }

  :deep(li) {
    @apply py-0.5;
  }

  :deep(strong) {
    @apply font-bold text-light-fg-emphasis;
  }

  :deep(hr) {
    @apply my-2 border-t border-light-bd-muted;
  }

  :deep(blockquote) {
    @apply my-1 border-l-2 border-light-bd-muted pl-3 italic text-light-fg-muted;
  }

  :deep(code) {
    @apply rounded bg-light-bg-inset px-1 py-0.5 text-xs;
  }

  :deep(pre) {
    @apply my-1 overflow-x-auto rounded bg-light-bg-inset px-4 py-3 leading-normal;
  }

  :deep(pre code) {
    @apply bg-transparent p-0;
  }

  :deep(table) {
    @apply my-2 w-full border-collapse text-sm;
  }

  :deep(th) {
    @apply border border-light-bd-muted bg-light-bg-inset px-3 py-1.5 text-left font-bold text-light-fg-emphasis;
  }

  :deep(td) {
    @apply border border-light-bd-muted px-3 py-1.5;
  }

  // 最初と最後の要素のマージンをリセット
  :deep(> :first-child) {
    @apply pt-0;
  }

  :deep(> :last-child:not(pre)) {
    @apply pb-0;
  }
}
</style>
